/**
 * Decision logic for completing a PO from the detail page (AS-11374).
 *
 * Lives in a helper for the same reason as `poReloadImages.ts`: the vue-shim
 * stubs `.vue` imports, so the component's getters cannot be invoked from a
 * spec. The component keeps only the wiring, and a source guard proves it
 * delegates here.
 *
 * The status rule is NOT here. It belongs to `PurchaseOrderModel.canMarkAsCompleted()`,
 * so the view and the model cannot disagree about which statuses may complete.
 *
 * Story: AS-11374 (restores the write-off path AS-10661 removed)
 */

export interface CompletionLine {
  id?: number | string;
  orderedQuantity: number;
  receivedQuantity?: number | null;
}

/**
 * Lines received below their ordered quantity. Uses the same per-line rule as
 * the page's `fullyReceivedItemsCount` chip (over-received counts as done), so
 * the warning's number agrees with what the merchant sees on the page.
 */
export function countOutstandingLines(items: readonly CompletionLine[] | null | undefined): number {
  if (!items) return 0;
  return items.filter((item) => (item.receivedQuantity || 0) < item.orderedQuantity).length;
}

/**
 * Lines still short once the quantities typed into the Receive modal are
 * received. Used by Receive & Complete, which completes in the same request,
 * so the write-off is whatever this receive leaves outstanding.
 */
export function countOutstandingAfterReceive(
  items: readonly CompletionLine[] | null | undefined,
  receiving: Readonly<Record<string, number | string | null | undefined>>,
): number {
  if (!items) return 0;
  return countOutstandingLines(
    items.map((item) => ({
      orderedQuantity: item.orderedQuantity,
      receivedQuantity:
        (item.receivedQuantity || 0) + (Number(item.id != null ? receiving[String(item.id)] : 0) || 0),
    })),
  );
}

/**
 * Header Complete button / More item. Outstanding lines do NOT block it: that
 * is the write-off case (supplier short-shipped, the rest will never arrive).
 * The confirmation dialog carries the warning instead. A PO with no lines has
 * nothing to complete.
 */
export function canOfferCompletion(
  statusAllowsCompletion: boolean,
  items: readonly CompletionLine[] | null | undefined,
): boolean {
  return statusAllowsCompletion && !!items && items.length > 0;
}

/**
 * The "Nothing left to receive?" callout inside the Receive modal. Narrower
 * than `canOfferCompletion` on purpose: its copy is only true once every line
 * is in, and an in-modal button shown while lines were still owed is what
 * AS-10661 recorded merchants clicking by mistake.
 */
export function isNothingLeftToReceive(
  statusAllowsCompletion: boolean,
  items: readonly CompletionLine[] | null | undefined,
): boolean {
  return canOfferCompletion(statusAllowsCompletion, items) && countOutstandingLines(items) === 0;
}

/** Copy matches atlas `ActionButtons.tsx` "Complete PO with unfulfilled items?". */
export function writeOffWarning(outstandingCount: number): string {
  const subject = outstandingCount === 1 ? '1 item has' : `${outstandingCount} items have`;
  return (
    `${subject} quantities still outstanding (not yet received). ` +
    'Marking this PO complete will write off the remaining quantity.'
  );
}
